import { UserBookingInterest } from "src/models/BookingInterest";
import { NotFoundError } from "src/models/exceptions/NotFoundError";
import { ProblemDetails } from "src/models/ProblemDetails";
import { BookingInterestsGroupedBySession } from "src/models/ticketingDashboard/BookingInterestsGroupedBySession";
import { Ticket } from "src/models/ticketingDashboard/Ticket";
import {
  BookingInterestsMinAgeOverrideResponse,
  BulkSendBookingInterestResponse,
  RevokeBookingInterestResponse,
  UpdateBookingInterestResponse,
  UserBookingInterestResponse
} from "src/models/types";
import { defaultFetch, fetchWithAuth } from "src/utils/auth";

export const readAllocationBookingInterests = async (allocationId: number): Promise<BookingInterestsGroupedBySession> => {
  const response = await fetchWithAuth(`/api/backend/v2/allocations/${allocationId}/sessions/booking-interests`);

  if (response.status === 403) {
    const problemDetails: ProblemDetails = await response.json();
    throw new Error(problemDetails.title, { cause: 403 });
  }

  if (response.status === 404) {
    throw new NotFoundError(await response.json() as ProblemDetails)
  }

  if (response.status >= 400 && response.status < 600) {
    throw new Error("Oops! It looks like there's a hiccup. Please try again or contact support if the issue persists.");
  }

  return await response.json() as BookingInterestsGroupedBySession;
}

export const revokeBookingInterest = async (
  bookingInterestId: number,
  parentBookingInterestId: number | undefined,
  allocationId: number,
  allocationSessionId: number
): Promise<RevokeBookingInterestResponse> => {
  try {
    const body: string = JSON.stringify({
      bookingInterestId,
      parentBookingInterestId,
      allocationId,
      allocationSessionId
    });
    const result = await fetchWithAuth(`/api/backend/v2/bookinginterests/${bookingInterestId}/revoke`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    });

    if (result.status === 200) {
      return new BulkSendBookingInterestResponse({ data: true });
    }

    if (result.status === 401) {
      return new RevokeBookingInterestResponse({ data: false, error: "You are unauthorized to rewoke the ticket." });
    }

    return new BulkSendBookingInterestResponse({ data: false, error: await result.json() });
  } catch (error) {
    console.error(error);
    return new RevokeBookingInterestResponse({ data: false, error: `Unable to revoke the ticket.` });
  }
}

export const normalizeAccessibility = (value: string | null | undefined) =>
  value === null || value === undefined || value === 'None'
    ? null
    : value

export const updateBookingInterest = async (
  allocationId: number,
  sessionId: number,
  reCreateTicket: boolean = false,
  allocationSessionId: number,
  ticket: Ticket
): Promise<UpdateBookingInterestResponse> => {
  try {
    const body: string = JSON.stringify({
      allocationId,
      sessionId,
      allocationSessionId,
      reCreateTicket,
      bookingInterest: {
        id: ticket.id,
        assignedFirstName: ticket.firstName,
        assignedLastName: ticket.lastName,
        email: ticket.email,
        userId: ticket.userId,
        userAgeAtBooking: ticket.age,
        user: {
          email: ticket.email,
          firstName: ticket.firstName,
          lastName: ticket.lastName,
          age: ticket.age,
          accessibility: normalizeAccessibility(ticket.accessibility),
          otherAccessibility: normalizeAccessibility(ticket.otherAccessibility),
        },
        accessibility: normalizeAccessibility(ticket.accessibility),
        otherAccessibility: normalizeAccessibility(ticket.otherAccessibility),
        relationshipToInvitee: ticket.relationshipToInvitee,
        relatedBookingInterests: ticket.guests.map(guest => ({
          id: guest.id,
          assignedFirstName: guest.firstName,
          assignedLastName: guest.lastName,
          userId: guest.userId,
          relationshipToInvitee: guest.relationshipToInvitee,
          userAgeAtBooking: guest.age,
          accessibility: normalizeAccessibility(guest.accessibility),
          otherAccessibility: normalizeAccessibility(guest.otherAccessibility),
          user: {
            firstName: guest.firstName,
            lastName: guest.lastName,
            age: guest.age,
            accessibility: normalizeAccessibility(guest.accessibility),
            otherAccessibility: normalizeAccessibility(guest.otherAccessibility),
          },
        }))
      }
    });
    const result = await fetchWithAuth(`/api/backend/v2/bookinginterests/${ticket.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    });

    if (result.status === 401) {
      return new UpdateBookingInterestResponse({ data: false, error: "You are unauthorized to update the ticket." });
    }

    const resultText = await result.json();
    if (result.status >= 400 && result.status < 600) {
      return new UpdateBookingInterestResponse({ data: false, error: resultText });
    }

    return new UpdateBookingInterestResponse({ data: resultText });
  } catch (error) {
    console.error(error);
    return new UpdateBookingInterestResponse({ data: false, error: `Unable to update the ticket.` });
  }
}

export const bulkSendBookingInterests = async (
  allocationId: number | undefined,
  sessionId: number,
  tickets: Ticket[]
): Promise<BulkSendBookingInterestResponse> => {
  try {
    const bookingInterests =
      tickets.map(ticket => ({
        assignedFirstName: ticket.firstName,
        assignedLastName: ticket.lastName,
        userId: ticket.userId,
        email: ticket.email,
        relationshipToInvitee: ticket.relationshipToInvitee,
        age: ticket.age,
        isGroupTicket: ticket.guests.length > 5,
        accessibility: ticket.accessibility === "None" ? null : ticket.accessibility,
        otherAccessibility: ticket.otherAccessibility,
        relatedBookingInterests: (ticket.guests ?? []).map(guest => ({
          assignedFirstName: guest.firstName,
          assignedLastName: guest.lastName,
          age: guest.age,
          accessibility: guest.accessibility === "None" ? null : guest.accessibility,
          otherAccessibility: guest.otherAccessibility,
          relationshipToInvitee: guest.relationshipToInvitee
        }))
      }))

    const url = `/api/backend/v2/booking-interests/bulk-create`;
    const body = JSON.stringify({
      allocationId,
      sessionId,
      bookingInterests
    })
    const result = await fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body
    });

    if (result.status === 201) {
      return new BulkSendBookingInterestResponse({ data: true });
    }

    if (result.status === 401 || result.status === 403) {
      return new BulkSendBookingInterestResponse({ data: false, error: "You are unauthorized to create the tickets." });
    }

    return new BulkSendBookingInterestResponse({ data: false, error: await result.json() });
  } catch (error) {
    console.error(error);
    return new BulkSendBookingInterestResponse({ data: false, error: `Unable to create the tickets.` });
  }
}

export const readExcludedEmailsForSession = async (sessionId: number): Promise<string[]> => {
  try {
    const result = await fetchWithAuth(`/api/backend/v2/sessions/${sessionId}/excluded-emails`);

    if (result.status === 200) {
      return await result.json();
    } else if (result.status === 401) {
      console.warn("User unauthorized to load excluded emails for session.");

      return [];
    } else {
      console.error("Unable to load excluded emails for session.", result);

      return [];
    }
  } catch (error) {
    console.error(error);

    return [];
  }
}

export const readBookingInterestsWithMinAgeOverrideRequests = async (showId: number): Promise<BookingInterestsMinAgeOverrideResponse> => {
  try {
    const fetchUrl = `/api/backend/v2/bookingInterests/minimum-age-override-requests/${showId}`;
    const res = await fetchWithAuth(fetchUrl, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (res.status >= 400 && res.status < 600) {
      return new BookingInterestsMinAgeOverrideResponse({ error: "Server not returned a valid response" });
    }

    const resJson = await res.json();
    return new BookingInterestsMinAgeOverrideResponse({ data: resJson?.bookingInterests });
  } catch (error) {
    let message;
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = "Unable to load booking interests";
    }

    return new BookingInterestsMinAgeOverrideResponse({ error: message });
  }
};

export interface ConsolidatedFlowActionResult {
  success: boolean;
  error?: string;
  newStatus?: string;
  /**
   * Set when the click changed nothing because the booking was already past that point —
   * already on the list, or already holding an offer. Shown instead of the caller's own
   * success wording, which would be describing something that did not happen.
   */
  message?: string;
}

const extractConsolidatedFlowError = async (response: Response, fallback: string): Promise<string> => {
  try {
    const problemDetails = await response.json();
    const message = problemDetails?.errors?.message;
    return (Array.isArray(message) ? message[0] : message) ?? problemDetails?.title ?? fallback;
  } catch {
    return fallback;
  }
};

/**
 * Second chance rejoin (TTT-687): after a timed-out offer the booking goes back into the
 * queue; after a missed check-in it joins the last min queue. Queue position stays the
 * original request timestamp either way.
 */
export const rejoinQueue = async (
  bookingInterestId: number,
  acknowledgedNoGuarantee: boolean,
  dependentBookingInterestIdsToKeep?: number[]
): Promise<ConsolidatedFlowActionResult> => {
  try {
    const response = await fetchWithAuth(`/api/backend/v1/bookingInterests/${bookingInterestId}/rejoin`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgedNoGuarantee, dependentBookingInterestIdsToKeep }),
    });

    if (response.status === 200) {
      return await response.json();
    }

    return { success: false, error: await extractConsolidatedFlowError(response, "Unable to rejoin the queue.") };
  } catch (error) {
    console.error(error);
    return { success: false, error: "Unable to rejoin the queue." };
  }
};

/** "Join the Last Min List" / "I'm No Longer Available" from the activation email (TTT-688 §5.3). */
export const joinLastMinQueue = async (
  bookingInterestId: number,
  decline: boolean = false
): Promise<ConsolidatedFlowActionResult> => {
  try {
    const response = await fetchWithAuth(`/api/backend/v1/bookingInterests/${bookingInterestId}/join-last-min`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decline }),
    });

    if (response.status === 200) {
      return await response.json();
    }

    return { success: false, error: await extractConsolidatedFlowError(response, "Unable to update your booking.") };
  } catch (error) {
    console.error(error);
    return { success: false, error: "Unable to update your booking." };
  }
};

export const readBookingInterestByIdOrTicketToken = async (
  tokenOrbookingInterestId: string | number,
  strategy: "accept" | "confirmAttendance"
): Promise<UserBookingInterestResponse> => {
  try {
    let response;
    if (typeof tokenOrbookingInterestId === "string" && strategy === "confirmAttendance") {
      const fetchUrl = `/api/backend/v1/bookingInterests/${tokenOrbookingInterestId}/confirm`;
      response = await defaultFetch(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else if (typeof tokenOrbookingInterestId === "string" && strategy === "accept") {
      const fetchUrl = `/api/backend/v1/bookingInterests/${tokenOrbookingInterestId}/accept`;
      response = await defaultFetch(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } else {
      const fetchUrl = `/api/backend/v1/bookingInterests/${tokenOrbookingInterestId}?strategy=${strategy}`;
      response = await fetchWithAuth(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }

    if (response.status === 400) {
      const problemDetails: ProblemDetails = await response.json();
      return new UserBookingInterestResponse({ error: problemDetails });
    }

    if (response.status === 401 || response.status === 403) {
      return new UserBookingInterestResponse({ error: "You are unauthorized to view this booking interest." });
    }

    if (response.status >= 500) {
      const problemDetails: ProblemDetails = await response.json();
      return new UserBookingInterestResponse({ error: problemDetails });
    }

    const bookingInterests: UserBookingInterest = await response.json();

    return new UserBookingInterestResponse({ data: bookingInterests });
  } catch (error) {
    let message;
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = "Unable to load ticket";
    }
    return new UserBookingInterestResponse({ error: message });
  }
};
